import { Box, Button, Stack } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { BikeleasingContractDetailsDTO, BikeleasingContractDetailsDTOStatusEnum, BikeleasingV2ConditionDTO } from 'probonio-shared-ui/api';
import { apis } from 'probonio-shared-ui/module/api';
import { useTenantID } from 'probonio-shared-ui/module/me';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLinkButton } from '../../component/button/ExternalLinkButton';
import { downloadResponseNative } from '../../utils/fileConversion/nativeFileDownload';
import { useBikeleasingPortalLinkBuilder } from './bikeleasingPortalLink';
import { BikeData } from './contract/BikeData';
import { InspectionSection } from './contract/InspectionSection';
import { LeasingData } from './contract/LeasingData';
import { YourAdvantages } from './contract/YourAdvantages';
import { useContractPdfAvailable } from './useContractPdfAvailable';

interface Props {
  contract: BikeleasingContractDetailsDTO;
  condition: BikeleasingV2ConditionDTO;
}

export const BikeleasingContractDetails: React.FC<Props> = ({ condition, contract }) => {
  const { t } = useTranslation('extensions');
  const { enqueueSnackbar } = useSnackbar();
  const getTenantId = useTenantID();
  const { pdfAvailable, isCheckingAvailability, markUnavailable } = useContractPdfAvailable(contract.id);

  const { mutate: downloadFileMutate, isPending: isDownloading } = useMutation({
    mutationFn: async (contractId: string) => {
      const response = await apis.bikeLeasingV2.downloadContractFile({ tenantId: getTenantId(), contractId }, { responseType: 'blob' });
      await downloadResponseNative(response, 'contract.pdf');
    },
    onError: (err, contractId) => {
      if (contractId !== contract.id) {
        return;
      } else if (err instanceof AxiosError && err.response?.status === 404) {
        markUnavailable();
      } else {
        enqueueSnackbar(t('bikeleasing_v2.contract.buttons.downloadError'), { variant: 'error' });
      }
    },
  });

  const isUnavailable = pdfAvailable === false;
  const buildBikeleasingPortalLink = useBikeleasingPortalLinkBuilder();

  return (
    <Box
      sx={{
        paddingX: 1,
        paddingBottom: 2,
      }}
    >
      <YourAdvantages contract={contract} condition={condition} />
      <Box
        sx={{
          pt: 1,
        }}
      >
        <LeasingData contract={contract} condition={condition} />
      </Box>
      <Box
        sx={{
          pt: 1,
        }}
      >
        <BikeData contract={contract} />
      </Box>
      <Box
        sx={{
          pt: 1,
        }}
      >
        <InspectionSection contract={contract} />
      </Box>
      {contract.status !== BikeleasingContractDetailsDTOStatusEnum.EmployeeApprovalRequired && (
        <Box
          sx={{
            pt: 1,
          }}
        >
          <Button
            loading={isDownloading || isCheckingAvailability}
            onClick={() => downloadFileMutate(contract.id)}
            variant="contained"
            fullWidth
            disabled={isUnavailable || isCheckingAvailability}
          >
            {isUnavailable ? t('bikeleasing_v2.contract.buttons.downloadNotAvailable') : t('bikeleasing_v2.contract.buttons.download')}
          </Button>
        </Box>
      )}
      {contract.status === BikeleasingContractDetailsDTOStatusEnum.EmployeeApprovalRequired && (
        <Stack
          direction="column"
          sx={{
            pt: 1,
            gap: 1,
          }}
        >
          <ExternalLinkButton
            href={buildBikeleasingPortalLink(`/user/contracts/${contract.id}?EMPLOYEE_SIGNATURE_ACCEPT=true`)}
            fullWidth
            variant="contained"
          >
            {t('bikeleasing_v2.contract.buttons.accept')}
          </ExternalLinkButton>
          <ExternalLinkButton href={buildBikeleasingPortalLink(`/user/contracts/${contract.id}?EMPLOYEE_SIGNATURE_REJECT=true`)} fullWidth>
            {t('bikeleasing_v2.contract.buttons.reject')}
          </ExternalLinkButton>
        </Stack>
      )}
    </Box>
  );
};
